// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_100,
  PERFECT_SCROLLBAR_MAX_280
} from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardRealEstate.json";

import "react-table/react-table.css";

function RealEstateDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const pipelineOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "70%" } },
      colors: [c.ReactifyChartColor1],
      dataLabels: { enabled: true, formatter: v => addCommas(v) },
      xaxis: {
        categories: data.pipelineStages.map(s => s.stage),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const pipelineValueOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor3],
      dataLabels: { enabled: true, formatter: v => `$${v}M` },
      xaxis: {
        categories: data.pipelineStages.map(s => s.stage),
        labels: {
          style: { colors: c.primaryColor },
          rotate: -20,
          rotateAlways: true
        }
      },
      yaxis: {
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const leadsOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.leadsBySource.map(l => l.source),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const domOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: 3, curve: "smooth" },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05
        }
      },
      colors: [c.ReactifyChartColor4],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.domTrend.months,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}d`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const priceBandOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, distributed: true } },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      dataLabels: { enabled: true },
      legend: { show: false },
      xaxis: {
        categories: data.priceBands.map(p => p.band),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const agentsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "re.col.agent" }),
        accessor: "name",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "re.col.listings" }), accessor: "listings" },
      {
        Header: formatMessage({ id: "re.col.volume" }),
        accessor: "volumeM",
        Cell: p => `$${p.value}M`
      },
      { Header: formatMessage({ id: "re.col.deals" }), accessor: "deals90d" }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--realestate">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.real-estate" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx lg="8" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        <IntlMessages id="re.hero.title" />
                      </h6>
                      <p className="text-muted small mb-0">{data.marketNote}</p>
                    </div>
                    <div className="mt-2 mt-lg-0">
                      <Button color="primary" size="sm" className="me-2">
                        <IntlMessages id="re.hero.new-listing" />
                      </Button>
                      <Button color="secondary" size="sm" outline>
                        <IntlMessages id="re.hero.tour" />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small text-uppercase">
                    <IntlMessages id="re.section.hot" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_100}>
                    <ul className="list-unstyled mb-0 small">
                      {data.hotListings.map((h, i) => (
                        <li key={i} className="mb-2 d-flex justify-content-between">
                          <span>{h.address}</span>
                          <span>
                            <Badge
                              color={h.status === "hot" ? "danger" : "success"}
                            >
                              {h.status}
                            </Badge>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.listingsSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.listings" />
                  </p>
                  <h4 className="mb-0">{k.activeListings}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-plus-circle text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.new" />
                  </p>
                  <h4 className="mb-0">{k.newListings7d}</h4>
                  <span className="text-muted small">7d</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-clock-o text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.dom" />
                  </p>
                  <h4 className="mb-0">{k.avgDom}d</h4>
                  <span className="text-success small">{k.domDelta}d</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-usd text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.pipeline" />
                  </p>
                  <h4 className="mb-0">${k.pipelineValueM}M</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-users text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.leads" />
                  </p>
                  <h4 className="mb-0">{k.leadsWeek}</h4>
                  <span className="text-success small">+{k.leadsDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-home text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="re.kpi.viewings" />
                  </p>
                  <h4 className="mb-0">{k.viewingsWeek}</h4>
                  <span className="text-muted small">
                    {k.conversionPct}% <IntlMessages id="re.kpi.cvr" />
                  </span>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="3" className="mb-4">
              {data.pipelineStages.map((s, i) => (
                <Card key={i} className="mb-3">
                  <CardBody className="py-3">
                    <div className="text-muted small text-uppercase">
                      {s.stage}
                    </div>
                    <h4 className="mb-0">{s.count}</h4>
                    <div className="text-success small">${s.valueM}M</div>
                  </CardBody>
                </Card>
              ))}
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.chart.pipeline-count" />
                  </CardTitle>
                  <Chart
                    options={pipelineOptions}
                    series={[
                      {
                        name: "Listings",
                        data: data.pipelineStages.map(s => s.count)
                      }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.chart.pipeline-value" />
                  </CardTitle>
                  <Chart
                    options={pipelineValueOptions}
                    series={[
                      {
                        name: "Value $M",
                        data: data.pipelineStages.map(s => s.valueM)
                      }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.chart.leads" />
                  </CardTitle>
                  <Chart
                    options={leadsOptions}
                    series={[
                      {
                        name: "Leads",
                        data: data.leadsBySource.map(l => l.leads)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.chart.dom" />
                  </CardTitle>
                  <Chart
                    options={domOptions}
                    series={[{ name: "DOM", data: data.domTrend.dom }]}
                    type="area"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.chart.price" />
                  </CardTitle>
                  <Chart
                    options={priceBandOptions}
                    series={[
                      {
                        name: "Count",
                        data: data.priceBands.map(p => p.count)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.section.viewings" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_280}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>
                            <IntlMessages id="re.col.when" />
                          </th>
                          <th>
                            <IntlMessages id="re.col.property" />
                          </th>
                          <th>
                            <IntlMessages id="re.col.agent" />
                          </th>
                          <th>
                            <IntlMessages id="re.col.type" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.viewings.map((v, i) => (
                          <tr key={i}>
                            <td className="text-nowrap text-muted">{v.when}</td>
                            <td>{v.property}</td>
                            <td>{v.agent}</td>
                            <td>
                              <Badge color="light" className="text-dark">
                                {v.type}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="re.table.agents" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.agents}
                    columns={agentsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default RealEstateDashboard;
